import React, { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { HeadphonesIcon, ImageIcon, FileText, BookOpen, ChevronDown, PenTool as Tool, Download, Mail, MessageCircle, Menu, X } from 'lucide-react';
import UserMenu from './UserMenu';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo.gif'; 

interface LayoutProps {
  user?: { name?: string; email?: string; } | null;
  onLogout: () => void;
}

export default function Layout({ user, onLogout }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, user: authUser, profile } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const enableLocalTeamEmail =
    import.meta.env.MODE === 'localdev' && import.meta.env.VITE_ENABLE_TEAM_EMAIL === 'true';
  
  // Temporary debugging - remove after issue is resolved
  console.log('Layout component - Current role:', role);
  console.log('Layout component - Auth user:', authUser);
  console.log('Layout component - localStorage userRole:', localStorage.getItem('userRole'));
  
  // Don't show header on certain paths
  const hideHeaderPaths = ['/login', '/signup', '/verify', '/unauthorized', '/learn-more'];
  const shouldShowHeader = !hideHeaderPaths.some(path => location.pathname.startsWith(path));
  
  // Compute display name for UserMenu
  let displayName = 'User';
  let displayEmail = '';
  if (profile?.fullName) {
  displayName = profile.fullName;
  displayEmail = profile.email || authUser?.email || '';
  } else if (authUser?.displayName) {
    displayName = authUser.displayName;
    displayEmail = authUser.email || '';
  } else if (user?.name) {
    displayName = user.name;
    displayEmail = user.email || '';
  } else if (authUser?.email) {
    displayName = authUser.email.split('@')[0];
    displayEmail = authUser.email;
  } else if (user?.email) {
    displayName = user.email.split('@')[0];
    displayEmail = user.email;
  }

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    closeMobileMenu();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#161513] to-[#161513] flex flex-col">
      {shouldShowHeader && (
        <>
          <header className="w-full bg-[#161513]/80 backdrop-blur-lg border-b border-[#0CB4E4]/20 py-3 px-6 sticky top-0 z-50">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              {/* Logo and brand */}
              <Link to="/dashboard" className="flex items-center gap-3">
                <img 
                  src={logo} 
                  alt="CR3ATE.AI" 
                  className="h-8" 
                />
                <h1 className="font-light text-xl text-[#0CB4E4] font-brand">CR3ATE.AI</h1>
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center gap-2">
                {/* Resources dropdown */}
                <div className="relative group">
                  <button
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-300 text-sm hover:text-white hover:bg-[#0CB4E4]/80 transition-colors"
                    title="Resources"
                  >
                    <BookOpen className="w-4 h-4" />
                    Resources
                    <ChevronDown className="w-4 h-4 group-hover:rotate-180 transition-transform" />
                  </button>
                  <div className="absolute right-0 mt-2 w-72 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-2 bg-[#161513] rounded-xl shadow-lg border border-[#0CB4E4]/30">
                      <Link
                        to="/guides/indoor-capture"
                        className="block px-4 py-2 text-gray-300 hover:text-white hover:bg-[#0CB4E4]/30 transition-colors text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#0CB4E4]" />
                          <span>Ground Capture Guide</span>
                        </div>
                      </Link>
                      <Link
                        to="/guides/drone-capture"
                        className="block px-4 py-2 text-gray-300 hover:text-white hover:bg-[#0CB4E4]/30 transition-colors text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#0CB4E4]" />
                          <span>Drone Capture Guide</span>
                        </div>
                      </Link>
                      <a
                        href="/cr3ate-plugin.pdf"
                        className="block px-4 py-2 text-gray-300 hover:text-white hover:bg-[#0CB4E4]/30 transition-colors text-sm"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-[#0CB4E4]" />
                          <span>Plugin setup guide</span>
                        </div>
                      </a>
                    </div>
                  </div>
                </div>
                
                {/* Tools dropdown */}
                {(role === 'creator' || role === 'admin') && (
                <div className="relative group">
                  <button
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-300 text-sm hover:text-white hover:bg-[#0CB4E4]/80 transition-colors"
                    title="Tools"
                  >
                    <Tool className="w-4 h-4" />
                    Tools
                    <ChevronDown className="w-4 h-4 group-hover:rotate-180 transition-transform" />
                  </button>
                  <div className="absolute right-0 mt-2 w-72 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-2 bg-[#161513] rounded-xl shadow-lg border border-[#0CB4E4]/30">
                      <a
                        href="https://ydrive-stratus-releases.s3.eu-central-1.amazonaws.com/Stratus-v0.7.0-win64.zip"
                        className="block px-4 py-2 text-gray-300 hover:text-white hover:bg-[#0CB4E4]/30 transition-colors text-sm"
                        download
                      >
                        <div className="flex items-center gap-2">
                          <Download className="w-4 h-4 text-[#0CB4E4]" />
                          <span>Unreal Engine Plugin</span>
                        </div>
                      </a>
                      
                      <a
                        href="https://apps.apple.com/us/app/cr3ate-ai/id6751804720"
                        className="block px-4 py-2 text-gray-300 hover:text-white hover:bg-[#0CB4E4]/30 transition-colors text-sm"
                        target="_blank"
                        rel="noopener noreferrer"
                      > 
                        <div className="flex items-center gap-2">
                          <Download className="w-4 h-4 text-[#0CB4E4]" />
                          <span>Cr3ate iPhone App</span>
                        </div>       
                      </a>
                      
                    </div>
                  </div>
                </div>
                )}
                {/* Gallery button */}
                <button
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-300 text-sm hover:text-white hover:bg-[#0CB4E4]/80 transition-colors"
                  onClick={() => navigate('/the-gallery')}
                  title="Gallery"
                >
                  <ImageIcon className="w-4 h-4" />
                  Gallery
                </button>
                
                {/* Customer Support button */}
                <button
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-300 text-sm hover:text-white hover:bg-[#0CB4E4]/80 transition-colors"
                  onClick={() => navigate('/support')}
                  title="Customer Support"
                >
                  <HeadphonesIcon className="w-4 h-4" />
                  Support
                </button>

                {/* Feedback button */}
                <button
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-300 text-sm hover:text-white hover:bg-[#0CB4E4]/80 transition-colors"
                  onClick={() => navigate('/feedback')}
                  title="Feedback"
                >
                  <MessageCircle className="w-4 h-4" />
                  Feedback
                </button>
                
                {enableLocalTeamEmail && (
                  <button className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-300 text-sm hover:text-white hover:bg-[#0CB4E4]/80 transition-colors" onClick={() => navigate('/team-email')}>
                    <Mail className="w-4 h-4" /> Send Email
                  </button>
                )}

                {/* Admin Dashboard button, only for admins */}
                {role === 'admin' && (
                    <button className="px-3 py-2 rounded-lg bg-[#0CB4E4] text-[#161513] text-sm hover:bg-[#0CB4E4]/90 transition-colors" onClick={() => navigate('/admin/users')}>
                      Admin Dashboard
                    </button>
                )}
                
                {/* User menu with improved display name */}
                <UserMenu user={{ name: displayName, email: displayEmail }} onLogout={onLogout} />
              </div>

              {/* Mobile Navigation */}
              <div className="lg:hidden flex items-center gap-2">
                {/* User menu - always visible on mobile */}
                <UserMenu user={{ name: displayName, email: displayEmail }} onLogout={onLogout} />
                
                {/* Hamburger menu button */}
                <button
                  onClick={handleMobileMenuToggle}
                  className="p-2 rounded-lg text-gray-300 hover:text-white hover:bg-[#0CB4E4]/80 transition-colors"
                  aria-label="Toggle menu"
                >
                  {isMobileMenuOpen ? (
                    <X className="w-6 h-6" />
                  ) : (
                    <Menu className="w-6 h-6" />
                  )}
                </button>
              </div>
            </div>
          </header>

          {/* Mobile Menu Overlay */}
          {isMobileMenuOpen && (
            <div 
              className="lg:hidden fixed inset-0 bg-black/50 z-40"
              onClick={closeMobileMenu}
            />
          )}

          {/* Mobile Menu */}
          <div className={`lg:hidden fixed top-[73px] right-6 w-80 max-w-[calc(100vw-2rem)] bg-[#161513]/95 backdrop-blur-lg border border-[#0CB4E4]/20 rounded-lg shadow-xl z-50 transform transition-all duration-300 origin-top-right ${
            isMobileMenuOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
          }`}>
            <div className="py-4 px-6 space-y-2 max-h-[80vh] overflow-y-auto">
              {/* Resources Section */}
              <div className="border-b border-[#0CB4E4]/20 pb-4">
                <div className="flex items-center gap-2 mb-3 text-[#0CB4E4] font-medium">
                  <BookOpen className="w-4 h-4" />
                  <span>Resources</span>
                </div>
                <div className="space-y-1 ml-6">
                  <Link
                    to="/guides/indoor-capture"
                    className="block py-2 text-gray-300 hover:text-white transition-colors"
                    onClick={closeMobileMenu}
                  >
                    Ground Capture Guide
                  </Link>
                  <Link
                    to="/guides/drone-capture"
                    className="block py-2 text-gray-300 hover:text-white transition-colors"
                    onClick={closeMobileMenu}
                  >
                    Drone Capture Guide
                  </Link>
                  <a
                    href="/cr3ate-plugin.pdf"
                    className="block py-2 text-gray-300 hover:text-white transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMobileMenu}
                  >
                    Plugin Setup Guide
                  </a>
                </div>
              </div>

              {/* Tools Section */}
              {(role === 'creator' || role === 'admin') && (
              <div className="border-b border-[#0CB4E4]/20 pb-4">
                <div className="flex items-center gap-2 mb-3 text-[#0CB4E4] font-medium">
                  <Tool className="w-4 h-4" />
                  <span>Tools</span>
                </div>
                <div className="space-y-1 ml-6">
                  <a
                    href="https://ydrive-stratus-releases.s3.eu-central-1.amazonaws.com/Stratus-v0.7.0-win64.zip"
                    className="block py-2 text-gray-300 hover:text-white transition-colors"
                    download
                    onClick={closeMobileMenu}
                  >
                    Unreal Engine Plugin
                  </a>
                  
                  <a
                    href="https://apps.apple.com/us/app/cr3ate-ai/id6751804720"
                    className="block py-2 text-gray-300 hover:text-white transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMobileMenu}
                  >
                    Cr3ate iPhone App
                  </a>
                  
                </div>
              </div>
              )}
              {/* Navigation Items */}
              <div className="space-y-1">
                <button
                  className="flex items-center gap-2 w-full py-3 text-gray-300 hover:text-white transition-colors"
                  onClick={() => handleNavigate('/the-gallery')}
                >
                  <ImageIcon className="w-4 h-4" />
                  Gallery
                </button>
                
                <button
                  className="flex items-center gap-2 w-full py-3 text-gray-300 hover:text-white transition-colors"
                  onClick={() => handleNavigate('/support')}
                >
                  <HeadphonesIcon className="w-4 h-4" />
                  Support
                </button>

                <button
                  className="flex items-center gap-2 w-full py-3 text-gray-300 hover:text-white transition-colors"
                  onClick={() => handleNavigate('/feedback')}
                >
                  <MessageCircle className="w-4 h-4" />
                  Feedback
                </button>
                
                {enableLocalTeamEmail && (
                  <button className="flex items-center gap-2 w-full py-3 text-gray-300 hover:text-white transition-colors" onClick={() => handleNavigate('/team-email')}>
                    <Mail className="w-4 h-4" /> Send Email
                  </button>
                )}

                {/* Admin Dashboard button, only for admins */}
                {role === 'admin' && (
                    <button className="flex items-center gap-2 w-full py-3 px-3 rounded-lg bg-[#0CB4E4] text-[#161513] hover:bg-[#0CB4E4]/90 transition-colors" onClick={() => handleNavigate('/admin/users')}>
                      Admin Dashboard
                    </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* Main content - Use Outlet instead of children */}
      <div className="flex-1">
        <Outlet />
      </div>
      
      {/* Footer */}
      {shouldShowHeader && (
       <footer className="w-full bg-[#161513]/80 backdrop-blur-lg border-t border-[#0CB4E4]/20 py-4 px-6">
         <div className="max-w-7xl mx-auto w-full">
           <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-gray-400 text-xs sm:text-sm gap-2 sm:gap-0">
       
             {/* Left (always one line) */}
             <p className="text-center sm:text-left leading-tight">
               Made with passion for those shaping the world in 3D
             </p>
       
             {/* Right: stacked on mobile, single line on desktop */}
             <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end text-center sm:text-right leading-tight gap-y-1 sm:gap-y-0 sm:gap-x-4">
               
               {/* Line 1: copyright */}
               <span>&copy; {new Date().getFullYear()} CR3ATE.AI by Ydrive Inc</span>
       
               {/* Line 2: terms + privacy + email */}
               <div className="flex justify-center sm:justify-end flex-wrap gap-x-3">
                 <Link to="/terms" className="hover:text-gray-300 transition-colors">Terms</Link>
                 <Link to="/privacy" className="hover:text-gray-300 transition-colors">Privacy</Link>
                 <a href="mailto:info@cr3ate.ai" className="hover:text-gray-300 transition-colors">info@cr3ate.ai</a>
               </div>
       
             </div>
           </div>
         </div>
       </footer>
      )}
    </div>
  );
}
